import { useEffect } from 'react';

import { syncEngine } from '../features/sync/engine';

// PB realtime auto-reconnects after a network blip / mobile sleep /
// backgrounded tab, but does NOT replay events emitted while the SSE
// channel was down. This hook nudges the engine to converge by calling
// `recover()` on the moments where stale state is most likely:
//
//   * `visibilitychange` → tab came back from background
//   * `online` → network reconnected
//   * a slow tick (90 s) while the tab is visible — belt-and-braces
//
// `engine.recover()` is a no-op when the engine isn't running, so this
// hook is harmless on devices without a sync code. The interval is
// suppressed while the document is hidden to avoid burning battery on
// background tabs. See #28.
const RECOVERY_TICK_MS = 30_000;

export function useEngineRecovery(): void {
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startInterval = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(() => {
        void syncEngine.recover();
      }, RECOVERY_TICK_MS);
    };

    const stopInterval = () => {
      if (intervalId === null) return;
      clearInterval(intervalId);
      intervalId = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void syncEngine.recover();
        startInterval();
      } else {
        stopInterval();
      }
    };

    const onOnline = () => {
      void syncEngine.recover();
    };

    if (document.visibilityState === 'visible') startInterval();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, []);
}
